import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading, Card, StatusBadge, EmptyState } from "@/components/common";
import {
  ClipboardList, Activity, GitPullRequestArrow, Gavel, FileStack, CalendarClock,
  Eye, AlertTriangle, Landmark, Building2, Scale, Wallet, ChevronRight, ExternalLink, Users,
} from "lucide-react";

const evtLabel = (t) => (t || "").replace(/_/g, " ");

function Section({ title, icon: Icon, children }) {
  return (
    <section className="mb-8" data-testid={`section-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="flex items-center gap-2 mb-3">
        <span className="h-6 w-6 rounded-md bg-brand-navy flex items-center justify-center"><Icon className="h-3.5 w-3.5 text-white" /></span>
        <h2 className="font-display text-sm font-bold uppercase tracking-wider text-brand-navy">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function Panel({ title, count, to, icon: Icon, children, testid }) {
  return (
    <Card className="flex flex-col" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4 text-brand-civic" />}
          <h3 className="font-display font-semibold text-brand-ink text-sm">{title}</h3>
          {count !== undefined && <span className="text-xs font-bold text-slate-400">{count}</span>}
        </div>
        {to && <Link to={to} className="text-xs font-medium text-brand-civic hover:text-brand-navy">View all</Link>}
      </div>
      <div className="flex-1 space-y-0.5">{children}</div>
    </Card>
  );
}

const Empty = ({ text }) => <p className="text-sm text-slate-400 px-2 py-1.5">{text}</p>;

function AsgRow({ a, showDue }) {
  return (
    <Link to={`/bills/${a.bill_id}`} className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-slate-50 group transition-colors" data-testid={`asg-row-${a.assignment_id}`}>
      <span className="font-mono text-xs font-semibold text-brand-civic w-14 shrink-0">{a.bill?.bill_number}</span>
      <span className="text-sm text-slate-700 truncate flex-1 group-hover:text-brand-navy">{a.bill?.title || a.bill_id}</span>
      {showDue && a.due_date && <span className="text-xs font-semibold text-red-500 shrink-0">{fmtDate(a.due_date)}</span>}
      <ChevronRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-brand-civic shrink-0" />
    </Link>
  );
}

function EventRow({ e }) {
  const inner = (
    <>
      <div className="flex items-center gap-2">
        {e.bill?.bill_number && <span className="font-mono text-xs font-semibold text-brand-civic">{e.bill.bill_number}</span>}
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 uppercase font-bold">{evtLabel(e.event_type)}</span>
        <span className="text-[10px] text-slate-400">{fmtDate(e.detected_at || e.date || e.event_date)}</span>
      </div>
      <p className="text-sm text-slate-600 truncate">{e.description || e.title || e.bill?.title}</p>
    </>
  );
  return e.bill_id
    ? <Link to={`/bills/${e.bill_id}`} className="block rounded-lg px-2 py-1.5 hover:bg-slate-50 transition-colors">{inner}</Link>
    : <div className="rounded-lg px-2 py-1.5">{inner}</div>;
}

function HubRow({ it }) {
  return (
    <a href={it.source_url ? (it.source_url.startsWith("http") ? it.source_url : `https://${it.source_url}`) : "#"} target="_blank" rel="noreferrer" className="flex items-start gap-2 rounded-lg px-2 py-1.5 hover:bg-slate-50 transition-colors">
      <span className="text-sm text-slate-700 truncate flex-1">{it.title}</span>
      {it.source_url && <ExternalLink className="h-3 w-3 text-slate-400 shrink-0 mt-1" />}
    </a>
  );
}

const HEALTH = { healthy: "bg-brand-teal", degraded: "bg-brand-gold", failed: "bg-red-500", unknown: "bg-slate-300", disabled: "bg-slate-300" };

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard/operations").then((r) => setD(r.data)).catch(() => setD(false)); }, []);
  if (d === null) return <Loading />;
  if (!d) return <EmptyState icon={Activity} title="Unable to load dashboard" />;
  const mw = d.my_work, la = d.legislative_activity, gov = d.government_information;
  const c = mw.counts;

  return (
    <div data-testid="operations-dashboard">
      <div className="mb-6">
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-brand-ink">Operations · {user?.name?.split(" ")[0]}</h1>
        <p className="text-slate-500 text-sm mt-1">Legislative command center · {d.data_mode?.toUpperCase()} data · role: {user?.role}.</p>
      </div>

      <Section title="My Work" icon={ClipboardList}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Panel title="Open Assignments" count={c.open} to="/assignments" icon={ClipboardList} testid="mw-open">
            {mw.open_assignments.length ? mw.open_assignments.map((a) => <AsgRow key={a.assignment_id} a={a} />) : <Empty text="No open assignments." />}
          </Panel>
          <Panel title="Needs Analysis" count={c.needs_analysis} icon={Activity} testid="mw-needs-analysis">
            {mw.needs_analysis.length ? mw.needs_analysis.map((a) => <AsgRow key={a.assignment_id} a={a} />) : <Empty text="Nothing awaiting analysis." />}
          </Panel>
          <Panel title="Pending Review" count={c.pending_review} icon={Eye} testid="mw-pending-review">
            {mw.pending_review.length ? mw.pending_review.map((a) => <AsgRow key={a.assignment_id} a={a} />) : <Empty text="Nothing pending review." />}
          </Panel>
          <Panel title="Recently Changed" count={c.recently_changed} icon={Activity} testid="mw-recently-changed">
            {mw.recently_changed.length ? mw.recently_changed.map((a) => <AsgRow key={a.assignment_id} a={a} />) : <Empty text="No recent changes on your bills." />}
          </Panel>
          <Panel title="Overdue" count={c.overdue} icon={AlertTriangle} testid="mw-overdue">
            {mw.overdue.length ? mw.overdue.map((a) => <AsgRow key={a.assignment_id} a={a} showDue />) : <Empty text="Nothing overdue." />}
          </Panel>
          <Panel title="Due Soon" count={c.due_soon} icon={CalendarClock} testid="mw-due-soon">
            {mw.due_soon.length ? mw.due_soon.map((a) => <AsgRow key={a.assignment_id} a={a} showDue />) : <Empty text="Nothing due in the next 14 days." />}
          </Panel>
        </div>
      </Section>

      <Section title="Legislative Activity" icon={Activity}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Panel title="Recent Bill Changes" count={la.recent_changes.length} icon={Activity} testid="la-recent-changes">
            {la.recent_changes.length ? la.recent_changes.map((e, i) => <EventRow key={i} e={e} />) : <Empty text="No recent changes." />}
          </Panel>
          <Panel title="Status Changes" count={la.status_changes.length} icon={Landmark} testid="la-status-changes">
            {la.status_changes.length ? la.status_changes.map((e, i) => <EventRow key={i} e={e} />) : <Empty text="No status changes." />}
          </Panel>
          <Panel title="New Amendments" count={la.new_amendments.length} icon={GitPullRequestArrow} testid="la-amendments">
            {la.new_amendments.length ? la.new_amendments.map((a, i) => (
              <Link key={i} to={`/bills/${a.bill_id}`} className="block rounded-lg px-2 py-1.5 hover:bg-slate-50">
                <div className="flex items-center gap-2"><span className="font-mono text-xs font-semibold text-brand-civic">{a.bill?.bill_number}</span><span className="text-sm text-slate-700">{a.number}</span></div>
                <p className="text-xs text-slate-400 truncate">{a.description}</p>
              </Link>
            )) : <Empty text="No recent amendments." />}
          </Panel>
          <Panel title="Recent Votes" count={la.recent_votes.length} icon={Gavel} testid="la-votes">
            {la.recent_votes.length ? la.recent_votes.map((v, i) => (
              <Link key={i} to={`/bills/${v.bill_id}`} className="block rounded-lg px-2 py-1.5 hover:bg-slate-50">
                <div className="flex items-center gap-2"><span className="font-mono text-xs font-semibold text-brand-civic">{v.bill?.bill_number}</span><span className="text-[10px] text-slate-400">{fmtDate(v.date)}</span></div>
                <p className="text-xs text-slate-600 truncate">{v.motion} {(v.yeas || v.nays) ? `(${v.yeas}-${v.nays})` : ""}</p>
              </Link>
            )) : <Empty text="No recent votes." />}
          </Panel>
          <Panel title="New Versions" count={la.new_versions.length} icon={FileStack} testid="la-versions">
            {la.new_versions.length ? la.new_versions.map((v, i) => (
              <Link key={i} to={`/bills/${v.bill_id}`} className="block rounded-lg px-2 py-1.5 hover:bg-slate-50">
                <div className="flex items-center gap-2"><span className="font-mono text-xs font-semibold text-brand-civic">{v.bill?.bill_number}</span></div>
                <p className="text-xs text-slate-600 truncate">{v.version_label || v.version_type}</p>
              </Link>
            )) : <Empty text="No new versions." />}
          </Panel>
        </div>
      </Section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Section title="Upcoming" icon={CalendarClock}>
          <Panel title="Committee & Scheduled Events" count={d.upcoming.events.length} icon={CalendarClock} testid="up-events">
            {d.upcoming.events.length ? d.upcoming.events.map((e, i) => (
              <div key={i} className="rounded-lg px-2 py-1.5 hover:bg-slate-50">
                <p className="text-sm text-slate-700 truncate">{e.title}</p>
                <p className="text-xs text-slate-400">{e.committee} · {fmtDate(e.event_date)}{e.bill?.bill_number ? ` · ${e.bill.bill_number}` : ""}</p>
              </div>
            )) : <Empty text="No upcoming events." />}
          </Panel>
        </Section>
        <Section title="Monitoring" icon={Eye}>
          <div className="grid grid-cols-1 gap-4">
            <Panel title="Recent Watchlist Matches" count={d.monitoring.watchlist_matches.length} to="/watchlists" icon={Eye} testid="mon-watchlist">
              {d.monitoring.watchlist_matches.length ? d.monitoring.watchlist_matches.map((m, i) => (
                <Link key={i} to={`/bills/${m.bill_id}`} className="block rounded-lg px-2 py-1.5 hover:bg-slate-50">
                  <div className="flex items-center gap-2"><span className="text-xs text-brand-civic font-medium">{m.watchlist_name}</span><span className="text-[10px] text-slate-400">{fmtDate(m.created_at)}</span></div>
                  <p className="text-xs text-slate-600 truncate">{m.description}</p>
                </Link>
              )) : <Empty text="No recent matches." />}
            </Panel>
            <Panel title="Priority Bills" count={d.monitoring.priority_bills.length} to="/bills?priority=high" icon={AlertTriangle} testid="mon-priority">
              {d.monitoring.priority_bills.length ? d.monitoring.priority_bills.map((b) => (
                <Link key={b.bill_id} to={`/bills/${b.bill_id}`} className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-slate-50 group">
                  <span className="font-mono text-xs font-semibold text-brand-civic w-14">{b.bill_number}</span>
                  <span className="text-sm text-slate-700 truncate flex-1 group-hover:text-brand-navy">{b.title}</span>
                  <StatusBadge status={b.status} />
                </Link>
              )) : <Empty text="No high-priority bills." />}
            </Panel>
          </div>
        </Section>
      </div>

      <Section title="Government Information" icon={Building2}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Panel title="Governor" count={gov.governor.length} to="/hub" icon={Landmark} testid="gov-governor">
            {gov.governor.length ? gov.governor.map((it, i) => <HubRow key={i} it={it} />) : <Empty text="No recent items." />}
          </Panel>
          <Panel title="Agencies" count={gov.agencies.length} to="/hub" icon={Building2} testid="gov-agencies">
            {gov.agencies.length ? gov.agencies.map((it, i) => <HubRow key={i} it={it} />) : <Empty text="No recent items." />}
          </Panel>
          <Panel title="REC / Budget" count={gov.rec_budget.length} to="/hub" icon={Wallet} testid="gov-rec-budget">
            {gov.rec_budget.length ? gov.rec_budget.map((it, i) => <HubRow key={i} it={it} />) : <Empty text="No recent items." />}
          </Panel>
          <Panel title="Court" count={gov.court.length} to="/hub" icon={Scale} testid="gov-court">
            {gov.court.length ? gov.court.map((it, i) => <HubRow key={i} it={it} />) : <Empty text="No recent items." />}
          </Panel>
        </div>
        <Card className="mt-4" data-testid="gov-source-health">
          <h3 className="font-display font-semibold text-brand-ink text-sm mb-3">Source Health</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {gov.source_health.map((c) => (
              <div key={c.key} className="flex items-center gap-2 rounded-lg border border-brand-line px-2.5 py-1.5">
                <span className={`h-2 w-2 rounded-full ${HEALTH[c.health] || "bg-slate-300"}`} />
                <span className="text-xs text-slate-600 truncate flex-1">{c.name}</span>
                <span className="text-[10px] text-slate-400 capitalize">{c.enabled ? c.health : "off"}</span>
              </div>
            ))}
          </div>
        </Card>
      </Section>

      {d.management && (
        <Section title="Management" icon={Users}>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <Card data-testid="mgmt-pending-reviews"><p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Pending Reviews</p><p className="font-display text-3xl font-extrabold text-brand-navy mt-1">{d.management.pending_reviews}</p></Card>
            <Card data-testid="mgmt-overdue"><p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Overdue</p><p className="font-display text-3xl font-extrabold text-red-500 mt-1">{d.management.overdue}</p></Card>
            <Card data-testid="mgmt-priority"><p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Priority Workload</p><p className="font-display text-3xl font-extrabold text-[#9A7212] mt-1">{d.management.priority_workload}</p></Card>
            <Card data-testid="mgmt-units"><p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Policy Units</p><p className="font-display text-3xl font-extrabold text-brand-navy mt-1">{Object.keys(d.management.by_policy_unit).length}</p></Card>
          </div>
          <Card data-testid="mgmt-by-user">
            <div className="flex items-center justify-between mb-3"><h3 className="font-display font-semibold text-brand-ink text-sm">Assignments by Staff</h3><Link to="/assignments" className="text-xs text-brand-civic hover:text-brand-navy">Team view</Link></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left border-b border-brand-line text-[11px] uppercase tracking-wider text-slate-400"><th className="py-2">Staff</th><th className="py-2 text-center">Open</th><th className="py-2 text-center">Pending Review</th><th className="py-2 text-center">Overdue</th><th className="py-2 text-center">Priority</th></tr></thead>
                <tbody className="divide-y divide-brand-line">
                  {d.management.by_user.map((r, i) => (
                    <tr key={i} className="hover:bg-slate-50"><td className="py-2"><span className="text-brand-ink font-medium">{r.user?.name || "—"}</span> <span className="text-xs text-slate-400 capitalize">{r.user?.role}</span></td>
                      <td className="py-2 text-center font-semibold text-brand-ink">{r.open}</td>
                      <td className="py-2 text-center">{r.pending_review}</td>
                      <td className={`py-2 text-center font-semibold ${r.overdue > 0 ? "text-red-500" : "text-slate-500"}`}>{r.overdue}</td>
                      <td className={`py-2 text-center font-semibold ${r.priority > 0 ? "text-[#9A7212]" : "text-slate-500"}`}>{r.priority}</td>
                    </tr>
                  ))}
                  {!d.management.by_user.length && <tr><td colSpan={5} className="py-4 text-center text-slate-400 text-sm">No assignments yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </Section>
      )}
    </div>
  );
}
